/**
 * Lobby logic that is worth testing without a DOM: the deep-link rule, and the host's auto-start
 * countdown.
 */
import { normalizeCode } from '@spades/shared';
import { isEmbedded } from '../bridge/embedded';
import type { TableView } from '../net/types-local';
import { GAME_PATH, SITE_ORIGIN } from './paths';

/* ------------------------------------------------------------- deep links */

/**
 * The table code carried by the URL, if any.
 *
 * `?join=` is the shareable form (it is what the Invite button copies); `?code=` is the older
 * alias and stays supported because links already exist with it. Both are normalised, so a code
 * pasted in lower case or with stray punctuation still resolves, and an unparseable one yields
 * null rather than a request the server is certain to reject.
 */
export function codeFromSearch(search: string): string | null {
  const p = new URLSearchParams(search);
  for (const key of ['join', 'code']) {
    const raw = p.get(key);
    if (!raw) continue;
    const norm = normalizeCode(raw);
    if (norm) return norm;
  }
  return null;
}

/**
 * The link the Invite button copies on the web: the game's own address, `/play`, since `/` is the
 * landing site. Inside Discord the invite dialog is the real path and this is only its fallback;
 * the iframe's origin is Discord's proxy, useless to anyone outside, so it points at the public site.
 */
export function inviteUrl(code: string | null | undefined, origin: string = isEmbedded() ? SITE_ORIGIN : location.origin): string {
  return code ? `${origin}${GAME_PATH}?join=${code}` : `${origin}${GAME_PATH}`;
}

/* --------------------------------------------------------- auto-start */

/** How long the host's table waits before dealing itself in. */
export const AUTO_START_MS = 10_000;

export interface AutoStartInput {
  table: TableView | null;
  /** Are we the host? Only the host client may send `start`, so only it counts down. */
  isHost: boolean;
  /** True once the host has pressed Start, or the match has otherwise begun. */
  started: boolean;
}

/**
 * Whether the countdown should be running.
 *
 * It waits for at least one *human* in a seat: a table of four bots counting itself down would
 * deal a game nobody asked for, and in Discord the Activity can be open with nobody seated yet.
 * Requiring a human also means the countdown starts when the first person sits, not when the room
 * is created, which is the moment that actually predicts a game.
 */
export function shouldCountDown(input: AutoStartInput): boolean {
  const { table, isHost, started } = input;
  if (!isHost || started || !table) return false;
  if (table.phase !== 'lobby') return false;
  return table.seats.some((s) => s.controller === 'human');
}

/** Seconds left, for the button label. Clamped so it never renders a negative. */
export function countdownSeconds(remainingMs: number): number {
  return Math.max(0, Math.ceil(remainingMs / 1000));
}

/* ------------------------------------------------------------- seat copy */

export interface SeatCardCopy {
  /** What the card says under the name. */
  tag: string;
  /** Whether tapping the card should sit us there. */
  canTake: boolean;
}

/**
 * The line under a seat card.
 *
 * An empty seat says "Sit here", because tapping it is now how a player moves around the table —
 * from one team to the other, or into the free chair beside a friend. It used to read "Bot will
 * fill", which described the seat instead of offering it; that fact still gets said, once, under
 * the host's Start button ("Empty seats are filled with bots when you start") where it answers the
 * question a host actually has. The invite lives on its own button below the seats.
 */
export function seatCardCopy(opts: {
  controller: 'human' | 'bot' | 'empty';
  connected: boolean;
  mine: boolean;
  team: 0 | 1;
  /** The viewer's own team, or null when they are not seated. */
  myTeam: 0 | 1 | null;
  phase: string;
}): SeatCardCopy {
  const { controller, connected, mine, team, myTeam } = opts;
  if (controller === 'empty') return { tag: 'Sit here', canTake: true };
  if (controller === 'bot') return { tag: 'Bot', canTake: !mine };
  if (mine) return { tag: 'You', canTake: false };

  // Once the viewer has a seat, say what the seat is *to them*. The HUD already talks in Us and
  // Them, and a colour is a worse answer to "who is that" than "your partner" — partners sit
  // across, which is the one thing a new player has to learn about the table shape. Blue and Red
  // are only right for someone with no side of their own.
  const relation = myTeam == null ? (team === 0 ? 'Blue' : 'Red') : team === myTeam ? 'Your partner' : 'Them';
  return { tag: `${relation}${connected ? '' : ' · away'}`, canTake: false };
}

/* -------------------------------------------------------------- spectator */

/**
 * A spectator is someone with no seat at a table that is already playing. Before the match starts
 * everyone unseated is just "not sitting yet", which the lobby already expresses through its open
 * seat cards, so the watching UI would only be noise there.
 */
export function isSpectating(opts: { seat: number | null; phase: string | undefined }): boolean {
  return opts.seat === null && opts.phase != null && opts.phase !== 'lobby';
}

/**
 * The server answers a mid-hand `sit` with an *error* whose code is `bad_phase`, having already
 * recorded the pending seat — so this particular failure is really a success that lands next hand.
 * Recognising it is what lets the UI say "Seated next hand" instead of showing a scary red toast.
 */
export function isQueuedSitError(code: string, msg: string): boolean {
  return /bad_phase/i.test(code) || /seated at the next hand/i.test(msg);
}
